import { memoryFactLabel } from "./memorySaveGate";
import type { MemoryStatus } from "./types";

/**
 * Loop f item HRES (TA-559). How a person settles a held `contact_memory` fact
 * (CLAUDE.md section 4, contradiction holds: "the fact is held entirely and
 * surfaced to a human"). Until this, nothing resolved one, so a held
 * contradiction stayed held for good.
 *
 *   keep       the held row becomes a normal active fact and every other live
 *              row it contradicts is superseded by it.
 *   supersede  the held row is superseded and the fact it contradicts stands:
 *              a standing row the gate had moved to `held` is active again.
 *
 * WHAT "THE ROW IT CONTRADICTS" IS. The schema links a held row to nothing (no
 * column says which row held which, callBrief.ts says the same), so it is read
 * the way the save gate groups facts, by `memoryFactLabel`: the live
 * (`active` or `held`) fact rows of one contact that answer the same label. A
 * row with no label (an inference, a fact with no `label: ` form) answers none
 * and is settled alone. Nothing here reads a value or a word of what the
 * customer said.
 *
 * WHEN SUPERSEDING DOES NOT END THE DISAGREEMENT. With one row left, that row
 * stands. With two or more left they still disagree, so they stay as they are
 * (still held) and the plan names them, for the operator to settle one by one:
 * "an unresolved fact is never 60% true" cuts both ways, and this never
 * promotes a row on a guess among several.
 *
 * Pure: it plans over the rows it is handed and writes nothing. The write is
 * `resolveHeldMemoryFact` (packages/db), which uses only the existing
 * `status` and `superseded_by` columns.
 */

export const MEMORY_RESOLVE_ACTIONS = ["keep", "supersede"] as const;
export type MemoryResolveAction = (typeof MEMORY_RESOLVE_ACTIONS)[number];

export const isMemoryResolveAction = (word: string): word is MemoryResolveAction =>
  (MEMORY_RESOLVE_ACTIONS as readonly string[]).includes(word);

/** The part of a stored row the plan reads. */
export interface MemoryResolveRow {
  readonly id: string;
  readonly content: string;
  readonly isInference: boolean;
  readonly evidenceKind: string;
  readonly status: MemoryStatus;
}

export interface MemoryResolvePlan {
  readonly action: MemoryResolveAction;
  readonly targetId: string;
  /** Rows moved from `held` to `active`. */
  readonly activate: readonly string[];
  /** Rows moved to `superseded`, with the row that replaced each (null: none stands in its place). */
  readonly supersede: readonly { readonly id: string; readonly by: string | null }[];
  /** Rows of the label left held, still disagreeing: only a supersede among three or more rows. */
  readonly stillHeld: readonly string[];
}

export type MemoryResolveResult =
  | { readonly ok: true; readonly plan: MemoryResolvePlan }
  | { readonly ok: false; readonly refusal: "target-missing" | "target-not-held" };

const live = (r: MemoryResolveRow): boolean => r.status === "active" || r.status === "held";

/**
 * The plan for settling `targetId` over `rows`, the contact's memory-layer
 * rows. A target that is not among them, or is not held, is refused by code
 * with no plan.
 */
export function planMemoryResolve(
  action: MemoryResolveAction,
  targetId: string,
  rows: readonly MemoryResolveRow[],
): MemoryResolveResult {
  const target = rows.find((r) => r.id === targetId);
  if (target === undefined) return { ok: false, refusal: "target-missing" };
  if (target.status !== "held") return { ok: false, refusal: "target-not-held" };

  const label = memoryFactLabel(target);
  const others =
    label === null
      ? []
      : rows.filter((r) => r.id !== target.id && live(r) && memoryFactLabel(r) === label).sort((a, b) => (a.id < b.id ? -1 : 1));

  if (action === "keep") {
    return {
      ok: true,
      plan: {
        action,
        targetId,
        activate: [target.id],
        supersede: others.map((r) => ({ id: r.id, by: target.id })),
        stillHeld: [],
      },
    };
  }

  const sole = others.length === 1 ? others[0]! : null;
  return {
    ok: true,
    plan: {
      action,
      targetId,
      activate: sole !== null && sole.status === "held" ? [sole.id] : [],
      supersede: [{ id: target.id, by: sole === null ? null : sole.id }],
      stillHeld: others.length >= 2 ? others.filter((r) => r.status === "held").map((r) => r.id) : [],
    },
  };
}
